(ns manage.ad-variant.schedule-modal
  (:require ["@mui/material/Alert$default" :as Alert]
            ["@mui/material/AlertTitle$default" :as AlertTitle]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Collapse$default" :as Collapse]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/SvgIcon$default" :as SvgIcon]
            ["@mui/material/Typography$default" :as Typography]
            ["moment-timezone" :as moment-tz]
            ["moment/min/moment-with-locales$default" :as moment]
            ["react-hook-form" :as rhf]
            ["src/svg/wpp/content/calendar.svg$default" :as Calendar]
            [cljs-bean.core :refer [->clj ->js]]
            [common.form :as form]
            [common.modal.view :refer [defmodal modal-v2]]
            [common.queries :refer [use-mutation]]
            [common.util :as util]
            [common.widgets.form-field :refer [form-field]]
            [lemonpi.ads-schema.interface :as ads-schema]
            [manage.ad-variant.queries :as q]
            [manage.ad-variant.widgets :as variant-widgets]
            [manage.adset.hooks :as ah]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$ defhook defui]]
            [uix.re-frame :refer [use-subscribe]]))

(def ^:private supported-set (set ads-schema/supported-timezones))

(defhook use-default-timezone []
  (let [{[first-country] :countries} (ah/use-adset)]
    (uix/use-memo #(some supported-set
                         [(some->> first-country (.zonesForCountry (.-tz moment-tz)) first)
                          util/user-timezone])
                  [first-country])))

(defui timezone-field [{:keys [name]}]
  ($ form-field {:label "Timezone"}
     ($ form/autocomplete-field
        {:name name
         :field-props {:options (map #(hash-map :label % :value %) ads-schema/supported-timezones)}})))

(defui date-time-field [{:keys [name label required?]}]
  ($ form-field {:label label
                 :required? required?}
     ($ form/form-date-time-picker
        {:name name
         :rules (when required? #js {:required true})
         :text-field-props {:clearable (not required?)}
         :field-props
         (fn [props]
           (let [{{:keys [onChange value]} :field} (->clj props)]
             {:value (when value (moment value))
              :onChange (fn [^js moment-value]
                          (onChange
                           (when moment-value
                             (.format moment-value
                                      (.-DATETIME_LOCAL_SECONDS moment/HTML5_FMT)))))}))})))

(defui schedule-summary-section [props]
  ($ Stack {:direction "row"
            :gap 1
            :sx #js {:backgroundColor "grey.80"
                     :borderRadius 1
                     :p 2}}
     ($ SvgIcon {:component Calendar
                 :inheritViewBox true})
     ($ Stack {:gap 1}
        ($ Typography {:fontWeight 600} "Selected Schedule:")
        ($ variant-widgets/schedule-summary props))))

(defui multiple-variants-warning [{:keys [count]}]
  ($ Alert {:severity "warning"}
     ($ AlertTitle (str "Updating " count " ad variant" (when (> count 1) "s")))
     ($ Typography
        "These ad variants have different scheduling. Saving will overwrite all existing schedules.")))

(defui schedule-fields [{:keys [prefix]}]
  (let [add-prefix #(cond->> % prefix (str prefix "."))
        timezone-name (add-prefix "timezone")
        {:keys [setValue]} (form/use-form-context)
        {:keys [timezone] :as scheduling} (->clj (rhf/useWatch
                                                  (if prefix #js {:name prefix} #js {})))
        default-timezone (use-default-timezone)]
    (uix/use-effect #(when-not timezone
                       (setValue timezone-name default-timezone))
                    [setValue timezone-name timezone default-timezone])
    ($ :<>
       ($ timezone-field {:name timezone-name})
       ($ date-time-field {:name (add-prefix "start")
                           :label "Start date"
                           :required? true})
       ($ date-time-field {:name (add-prefix "end")
                           :label "End date (Optional)"})
       ($ Collapse {:in (boolean timezone)
                    :mountOnEnter true
                    :unmountOnExit true}
          ($ schedule-summary-section scheduling)))))

(defhook use-save-schedule [{:keys [ad-variants]}]
  (let [{:keys [advertiser-id adset-id]} (use-subscribe [:url-context])
        {set-scheduling :mutate
         is-pending :isPending} (use-mutation (q/set-scheduling-mutation
                                               {:advertiser-id advertiser-id
                                                :adset-id adset-id}))
        save-schedule (uix/use-callback
                       (fn [scheduling]
                         (set-scheduling
                          {:ad-uuids (mapv #(str (:uuid %)) ad-variants)
                           :scheduling (util/filter-nils-map scheduling)}
                          #js {:onSuccess #(rf/dispatch [:modal/close])}))
                       [ad-variants set-scheduling])]
    {:save-schedule save-schedule
     :is-saving is-pending}))

(defui modal-footer [{:keys [ad-variants]}]
  (let [{:keys [^js formState handleSubmit]} (form/use-form-context)
        is-submitting (.-isSubmitting formState)
        {:keys [save-schedule is-saving]} (use-save-schedule {:ad-variants ad-variants})
        on-submit (uix/use-callback (fn [data] (save-schedule (->clj data))) [save-schedule])]
    ($ :<>
       ($ Button {:variant "outlined"
                  :onClick #(rf/dispatch [:modal/close])}
          "Cancel")
       ($ Button {:variant "contained"
                  :disabled (or is-submitting is-saving)
                  :onClick (handleSubmit on-submit)}
          "Save schedule"))))

(defui schedule-modal-editable-view [{:keys [ad-variants]}]
  (let [all-same-scheduling (apply = (map :scheduling ad-variants))
        variant-count (count ad-variants)
        form-control (form/use-form
                      #js {:mode "onBlur"
                           :defaultValues (when all-same-scheduling
                                            (->js (:scheduling (first ad-variants))))})]
    ($ rhf/FormProvider (->js form-control)
       ($ modal-v2 {:title (str "Schedule Ad variant" (when (> variant-count 1) "s"))
                    :footer ($ modal-footer {:ad-variants ad-variants})}
          ($ Stack {:spacing 3
                    :sx #js {:minWidth 600}}
             (when-not all-same-scheduling
               ($ multiple-variants-warning {:count variant-count}))
             ($ Typography
                "Set when your ad should run. You can choose a start date only, or set a complete date range.")
             ($ schedule-fields))))))

(defui schedule-modal-read-only-view [{:keys [ad-variants]}]
  (let [scheduling (:scheduling (first ad-variants))]
    ($ modal-v2 {:title "Schedule Ad variant"
                 :footer ($ Button {:variant "outlined"
                                    :onClick #(rf/dispatch [:modal/close])}
                            "Close")}
       (if scheduling
         ($ Stack {:spacing 3
                   :sx #js {:minWidth 600}}
            ($ Alert {:severity "info"}
               "Scheduling comes from the feed")
            ($ schedule-summary-section scheduling))
         ($ Alert {:severity "info"}
            "No schedule configured")))))

(defmodal schedule-modal :ad-variants/schedule-modal
  [{:keys [ad-variants read-only]}]
  (if read-only
    ($ schedule-modal-read-only-view {:ad-variants ad-variants})
    ($ schedule-modal-editable-view {:ad-variants ad-variants})))
